import { ReactNode } from 'react';
import styles from './docs.module.css';

/* ============================================================================
   CodeBlock Component
   Displays syntax-highlighted code with copy functionality
   ============================================================================ */

interface CodeBlockProps {
	children: string;
	language?: 'bash' | 'json' | 'typescript' | 'javascript' | 'http';
	title?: string;
	showLineNumbers?: boolean;
}

export function CodeBlock({
	children,
	language = 'bash',
	title,
	showLineNumbers = false,
}: CodeBlockProps) {
	const copyToClipboard = () => {
		navigator.clipboard.writeText(children);
	};

	const lines = children.trim().split('\n');

	return (
		<div className={styles.codeBlock}>
			{title && (
				<div className={styles.codeBlockHeader}>
					<span className={styles.codeBlockTitle}>{title}</span>
					<button
						onClick={copyToClipboard}
						className={styles.copyButton}
						title="Copy to clipboard"
					>
						<svg
							width="16"
							height="16"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
						>
							<rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
							<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
						</svg>
					</button>
				</div>
			)}
			<div className={styles.codeBlockContent}>
				{!title && (
					<button
						onClick={copyToClipboard}
						className={styles.copyButtonFloating}
						title="Copy to clipboard"
					>
						<svg
							width="16"
							height="16"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
						>
							<rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
							<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
						</svg>
					</button>
				)}
				<pre className={styles.pre} data-language={language}>
					<code className={styles.code}>
						{showLineNumbers
							? lines.map((line, i) => (
									<span key={i} className={styles.codeLine}>
										<span className={styles.lineNumber}>{i + 1}</span>
										<span className={styles.lineContent}>{line}</span>
									</span>
								))
							: children.trim()}
					</code>
				</pre>
			</div>
		</div>
	);
}

/* ============================================================================
   Endpoint Component
   Displays API endpoint information with method badge
   ============================================================================ */

interface EndpointProps {
	method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
	path: string;
	description?: string;
}

export function Endpoint({ method, path, description }: EndpointProps) {
	const methodColors: Record<string, string> = {
		GET: styles.methodGet,
		POST: styles.methodPost,
		PUT: styles.methodPut,
		DELETE: styles.methodDelete,
		PATCH: styles.methodPatch,
	};

	return (
		<div className={styles.endpoint}>
			<div className={styles.endpointMain}>
				<span className={`${styles.method} ${methodColors[method]}`}>
					{method}
				</span>
				<code className={styles.endpointPath}>{path}</code>
			</div>
			{description && (
				<p className={styles.endpointDescription}>{description}</p>
			)}
		</div>
	);
}

/* ============================================================================
   Alert Component
   Displays info, warning, success, or error messages
   ============================================================================ */

interface AlertProps {
	variant: 'info' | 'warning' | 'success' | 'error';
	title?: string;
	children: ReactNode;
}

export function Alert({ variant, title, children }: AlertProps) {
	const icons: Record<string, ReactNode> = {
		info: (
			<svg
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
			>
				<circle cx="12" cy="12" r="10" />
				<line x1="12" y1="16" x2="12" y2="12" />
				<line x1="12" y1="8" x2="12.01" y2="8" />
			</svg>
		),
		warning: (
			<svg
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
			>
				<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
				<line x1="12" y1="9" x2="12" y2="13" />
				<line x1="12" y1="17" x2="12.01" y2="17" />
			</svg>
		),
		success: (
			<svg
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
			>
				<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
				<polyline points="22 4 12 14.01 9 11.01" />
			</svg>
		),
		error: (
			<svg
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
			>
				<circle cx="12" cy="12" r="10" />
				<line x1="15" y1="9" x2="9" y2="15" />
				<line x1="9" y1="9" x2="15" y2="15" />
			</svg>
		),
	};

	const variantStyles: Record<string, string> = {
		info: styles.alertInfo,
		warning: styles.alertWarning,
		success: styles.alertSuccess,
		error: styles.alertError,
	};

	return (
		<div className={`${styles.alert} ${variantStyles[variant]}`}>
			<div className={styles.alertIcon}>{icons[variant]}</div>
			<div className={styles.alertContent}>
				{title && <div className={styles.alertTitle}>{title}</div>}
				<div className={styles.alertBody}>{children}</div>
			</div>
		</div>
	);
}

/* ============================================================================
   Badge Component
   Displays small status badges
   ============================================================================ */

interface BadgeProps {
	variant: 'sandbox' | 'live' | 'required' | 'optional' | 'deprecated' | 'new';
	children?: ReactNode;
}

export function Badge({ variant, children }: BadgeProps) {
	const labels: Record<string, string> = {
		sandbox: 'Sandbox',
		live: 'Live',
		required: 'Required',
		optional: 'Optional',
		deprecated: 'Deprecated',
		new: 'New',
	};

	const variantStyles: Record<string, string> = {
		sandbox: styles.badgeSandbox,
		live: styles.badgeLive,
		required: styles.badgeRequired,
		optional: styles.badgeOptional,
		deprecated: styles.badgeDeprecated,
		new: styles.badgeNew,
	};

	return (
		<span className={`${styles.badge} ${variantStyles[variant]}`}>
			{children || labels[variant]}
		</span>
	);
}

/* ============================================================================
   Table Component
   Displays data in a styled table
   ============================================================================ */

interface TableColumn {
	key: string;
	header: string;
	width?: string;
	render?: (value: unknown, row: Record<string, unknown>) => ReactNode;
}

interface TableProps {
	columns: TableColumn[];
	data: Record<string, unknown>[];
	striped?: boolean;
}

export function Table({ columns, data, striped = true }: TableProps) {
	return (
		<div className={styles.tableWrapper}>
			<table className={`${styles.table} ${striped ? styles.tableStriped : ''}`}>
				<thead>
					<tr>
						{columns.map((col) => (
							<th key={col.key} style={col.width ? { width: col.width } : undefined}>
								{col.header}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{data.map((row, rowIndex) => (
						<tr key={rowIndex}>
							{columns.map((col) => (
								<td key={col.key}>
									{col.render
										? col.render(row[col.key], row)
										: (row[col.key] as ReactNode)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

/* ============================================================================
   Additional Helper Components
   ============================================================================ */

interface ParamListProps {
	children: ReactNode;
}

export function ParamList({ children }: ParamListProps) {
	return <div className={styles.paramList}>{children}</div>;
}

interface ParamProps {
	name: string;
	type: string;
	required?: boolean;
	children: ReactNode;
}

export function Param({ name, type, required = false, children }: ParamProps) {
	return (
		<div className={styles.param}>
			<div className={styles.paramHeader}>
				<code className={styles.paramName}>{name}</code>
				<span className={styles.paramType}>{type}</span>
				{required && <Badge variant="required" />}
				{!required && <Badge variant="optional" />}
			</div>
			<div className={styles.paramDescription}>{children}</div>
		</div>
	);
}

interface StepProps {
	number: number;
	title: string;
	children: ReactNode;
}

export function Step({ number, title, children }: StepProps) {
	return (
		<div className={styles.step}>
			<div className={styles.stepHeader}>
				<span className={styles.stepNumber}>{number}</span>
				<h3 className={styles.stepTitle}>{title}</h3>
			</div>
			<div className={styles.stepContent}>{children}</div>
		</div>
	);
}

interface CardProps {
	title: string;
	description?: string;
	href?: string;
	icon?: ReactNode;
	children?: ReactNode;
}

export function Card({ title, description, href, icon, children }: CardProps) {
	const content = (
		<>
			{icon && <div className={styles.cardIcon}>{icon}</div>}
			<div className={styles.cardContent}>
				<h3 className={styles.cardTitle}>{title}</h3>
				{description && <p className={styles.cardDescription}>{description}</p>}
				{children}
			</div>
		</>
	);

	if (href) {
		return (
			<a href={href} className={styles.card}>
				{content}
			</a>
		);
	}

	return <div className={styles.card}>{content}</div>;
}

interface CardGridProps {
	children: ReactNode;
	columns?: 2 | 3 | 4;
}

export function CardGrid({ children, columns = 2 }: CardGridProps) {
	return (
		<div
			className={styles.cardGrid}
			style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
		>
			{children}
		</div>
	);
}

/* ============================================================================
   Diagram Component
   Displays flow diagrams with styled boxes and arrows
   ============================================================================ */

interface DiagramStep {
	label: string;
	description?: string;
	highlight?: boolean;
}

interface DiagramProps {
	title?: string;
	steps: DiagramStep[];
	direction?: 'horizontal' | 'vertical';
}

export function Diagram({ title, steps, direction = 'vertical' }: DiagramProps) {
	return (
		<div className={styles.diagram}>
			{title && <div className={styles.diagramTitle}>{title}</div>}
			<div
				className={`${styles.diagramFlow} ${direction === 'horizontal' ? styles.diagramHorizontal : styles.diagramVertical}`}
			>
				{steps.map((step, index) => (
					<div key={index} className={styles.diagramStepWrapper}>
						<div
							className={`${styles.diagramStep} ${step.highlight ? styles.diagramStepHighlight : ''}`}
						>
							<div className={styles.diagramStepLabel}>{step.label}</div>
							{step.description && (
								<div className={styles.diagramStepDescription}>
									{step.description}
								</div>
							)}
						</div>
						{index < steps.length - 1 && (
							<div
								className={`${styles.diagramArrow} ${direction === 'horizontal' ? styles.diagramArrowHorizontal : styles.diagramArrowVertical}`}
							>
								<svg
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									{direction === 'horizontal' ? (
										<path d="M5 12h14M12 5l7 7-7 7" />
									) : (
										<path d="M12 5v14M5 12l7 7 7-7" />
									)}
								</svg>
							</div>
						)}
					</div>
				))}
			</div>
		</div>
	);
}

/* ============================================================================
   FlowDiagram Component
   Displays multi-column flow diagrams for complex integrations
   ============================================================================ */

interface FlowColumn {
	title: string;
	color?: 'primary' | 'secondary' | 'accent';
}

interface FlowStep {
	column: number;
	label: string;
	arrow?: 'right' | 'left' | 'down' | 'bidirectional';
	arrowLabel?: string;
}

interface FlowDiagramProps {
	title?: string;
	columns: FlowColumn[];
	steps: FlowStep[];
}

export function FlowDiagram({ title, columns, steps }: FlowDiagramProps) {
	const columnColors: Record<string, string> = {
		primary: styles.flowColumnPrimary,
		secondary: styles.flowColumnSecondary,
		accent: styles.flowColumnAccent,
	};

	return (
		<div className={styles.flowDiagram}>
			{title && <div className={styles.flowDiagramTitle}>{title}</div>}
			<div className={styles.flowDiagramContainer}>
				{/* Column Headers */}
				<div
					className={styles.flowDiagramHeaders}
					style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
				>
					{columns.map((col, index) => (
						<div
							key={index}
							className={`${styles.flowDiagramHeader} ${columnColors[col.color || 'primary']}`}
						>
							{col.title}
						</div>
					))}
				</div>
				{/* Flow Steps */}
				<div className={styles.flowDiagramSteps}>
					{steps.map((step, index) => (
						<div
							key={index}
							className={styles.flowDiagramRow}
							style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
						>
							{columns.map((_, colIndex) => (
								<div key={colIndex} className={styles.flowDiagramCell}>
									{step.column === colIndex && (
										<div className={styles.flowDiagramStepBox}>
											<span className={styles.flowDiagramStepNumber}>
												{index + 1}
											</span>
											<span className={styles.flowDiagramStepLabel}>
												{step.label}
											</span>
										</div>
									)}
									{step.arrow && step.column === colIndex && (
										<div
											className={`${styles.flowDiagramArrow} ${styles[`flowArrow${step.arrow.charAt(0).toUpperCase() + step.arrow.slice(1)}`]}`}
										>
											{step.arrowLabel && (
												<span className={styles.flowDiagramArrowLabel}>
													{step.arrowLabel}
												</span>
											)}
										</div>
									)}
								</div>
							))}
						</div>
					))}
				</div>
			</div>
		</div>
	);
}
